import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { T, useT, type TranslationKey } from "@/lib/translation";
import { Copy, Check, Gift } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getReferralStatus } from "@/api/client";
import type { ReferralEntryStatus } from "@/api/client";

type ReferralModalProps = {
  children?: ReactNode;
  externalOpen?: boolean;
  onExternalOpenHandled?: () => void;
};

// Trans' own key typing resolves to `never` with this project's non-strict TS
// config, so keys are checked here against the English files and cast once.

const STEPS: { key: ReferralEntryStatus; labelKey: TranslationKey }[] = [
  { key: "registered", labelKey: "onboarding:referral.steps.registered" },
  { key: "in_trial", labelKey: "onboarding:referral.steps.inTrial" },
  { key: "subscribed", labelKey: "onboarding:referral.steps.subscribed" },
];

const STATUS_STEP: Record<ReferralEntryStatus, number> = {
  registered: 1,
  in_trial: 2,
  subscribed: 3,
};

function ReferralProgress({ status }: { status: ReferralEntryStatus }) {
  const t = useT();
  const reached = STATUS_STEP[status];
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex gap-2">
        {STEPS.map((step, i) => (
          <div
            key={step.key}
            className={`h-2 flex-1 rounded-full transition-colors ${
              i < reached
                ? status === "subscribed"
                  ? "bg-green-500"
                  : "bg-primary"
                : "bg-muted"
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-muted-foreground">
        {t(STEPS[reached - 1].labelKey)}
      </span>
    </div>
  );
}

export function ReferralModal({
  children,
  externalOpen,
  onExternalOpenHandled,
}: ReferralModalProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (externalOpen) {
      setOpen(true);
      onExternalOpenHandled?.();
    }
  }, [externalOpen, onExternalOpenHandled]);

  const { data, isLoading } = useQuery({
    queryKey: ["referral-status-v2"],
    queryFn: getReferralStatus,
    enabled: open,
    staleTime: 0,
  });

  const inviteUrl = data
    ? `${window.location.origin}/signup?ref=${data.referral_code}`
    : "";

  const handleCopy = async () => {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ?? (
          <Button variant="ghost" className="w-full justify-start">
            <Gift className="h-4 w-4 mr-2" />
            {t("onboarding:referral.title")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("onboarding:referral.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            <T
              k="onboarding:referral.intro"
              components={{ strong: <strong /> }}
            />
          </p>

          <div className="flex gap-2">
            <Input
              readOnly
              value={inviteUrl}
              className="font-mono text-xs"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
              aria-label={t("onboarding:referral.copyLinkLabel")}
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>

          {isLoading ? (
            <div className="h-16 flex items-center justify-center">
              <span className="text-sm text-muted-foreground">
                {t("common:status.loading")}
              </span>
            </div>
          ) : data ? (
            <>
              <div className="bg-muted rounded-lg px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t("onboarding:referral.monthsEarned")}
                </span>
                <span className="text-base font-semibold tabular-nums">
                  {data.months_earned}
                </span>
              </div>

              <div>
                <p className="text-sm font-medium mb-3">
                  {t("onboarding:referral.yourReferrals")}
                </p>

                {data.referrals.length > 0 ? (
                  <div className="flex flex-col gap-3 max-h-48 overflow-y-auto pr-1">
                    {data.referrals.map((r, i) => (
                      <div
                        key={r.id}
                        className="flex items-center gap-4 text-sm"
                      >
                        <span
                          className="text-muted-foreground truncate w-44 shrink-0"
                          title={r.email}
                        >
                          {r.email ??
                            t("onboarding:referral.friendFallback", {
                              number: i + 1,
                            })}
                        </span>
                        <ReferralProgress status={r.status} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t("onboarding:referral.noReferrals")}
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
